import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as yup from "yup";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import NewPhoneNumberComponent from "../../../common/components/new-phone-input-component/new-phone-input-component";
import { preRegisterPartner } from "../../../api/become-partner";
import PlannerSuccessModal from "../../../common/components/planner-success-modal/PlannerSuccessModal";
import { joiEmailSchema } from "../../../utils/utils";
import ToastMessage from "../../../common/components/toast-message/toast-message";
import "./become-partner-form.scss";

const validationSchema = yup.object({
  businessName: yup.string()
  .matches(/^[a-zA-Z\s]*$/, "Business Name can only contain letters and spaces.")
  .required("Business Name is required"),
  businessType: yup.string().required("Business Type is required"),
  businessPhone: yup
    .string()
    .required("Business Phone is required")
    .min(10, "Enter a valid phone number"),
  businessEmail: yup
    .string()
    .trim()
    .email("Enter a valid email")
    .required("Business Email is required")
    .test("joi-email-check", "Invalid email format", function (value) {
          if (!value) return false;
          const { error } = joiEmailSchema.validate(value);
          return !error;
        }),
  message: yup.string(),
});

export const businessTypes = [
  {
    value: "Funeral & Memorial Service Providers",
    type: "Funeral & Memorial Service Providers",
  },
  {
    value: "Preneed Planner",
    type: "Preneed Planner",
  },
  { value: "Aftercare & Grief Support", type: "Aftercare & Grief Support" },
  { value: "Transportation & Logistics", type: "Transportation & Logistics" },
  { value: "Products & Supplies", type: "Products & Supplies" },
  {
    value: "Cremation Services",
    type: "Cremation Services",
  },
  { value: "Technology & Media", type: "Technology & Media" },
  {
    value: "Religious & Cultural Services",
    type: "Religious & Cultural Services",
  },
  {
    value: "Vendor Services",
    type: "Vendor Services",
  },
];

const compactInputProps = {
  sx: {
    height: "42px",
    "& .MuiInputBase-root": {
      height: "42px",
    },
    "& input": {
      padding: "10px 14px !important",
      height: "42px",
      boxSizing: "border-box",
    },
    "& .MuiSelect-select": {
      padding: "10px 14px",
      height: "42px",
      display: "flex",
      alignItems: "center",
    },
    "& fieldset": {
      height: "100%",
    },
  },
};

const BecomePartnerForm = () => {
  const [countryCode, setCountryCode] = useState<string>("+1");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  return (
    <Box
      className="become-partner-form-container"
      sx={{
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(0, 0, 0, 0.23)",
      },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(0, 0, 0, 0.23)",
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
        {
        borderWidth: "1px",
        borderColor: "rgba(0, 0, 0, 0.23)",
        },
      "& .MuiFormHelperText-root": {
        margin: 0,
        padding: 0,
        lineHeight: "16px",
      },
      paddingTop: { xs: "10px", md: "20px" },
      paddingBottom: { xs: "10px", md: "20px" },
      }}
    >
      <Box
        className="form-section"
        sx={{
          boxShadow: 3,
          borderRadius: "14px",
          fontFamily: "Roboto",
          maxHeight: "1025px",
          overflow: "hidden",
        }}
      >
        <Typography
          variant="h5"
          color="#104145"
          sx={{
            fontSize: { xs: "22px", md: "28px" },
            fontWeight: 500,
            lineHeight: { xs: "30px", md: "40px" },
            textAlign: "center",
            margin: "0 auto !important",
            width: "90%",
            marginBottom: "10px !important",
          }}
        >
          Interested in Becoming a MemorialGo Partner?
        </Typography>
        <Typography color="#104145" sx={{
            fontSize: { xs: "13px", md: "14px" },
            fontWeight: 500,
            textAlign: "center",
            margin: "0 auto !important",
            width: "90%",
            marginBottom: "20px !important",
          }}>Share a few details and our team will guide you through the onboarding process.</Typography>
        <Formik
          initialValues={{
            businessName: "",
            businessType: "",
            businessPhone: "",
            businessEmail: "",
            message: "",
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { resetForm, setFieldError }) => {
            setIsSubmitting(true);
            try {
              const preRegisterData = {
                businessName: values.businessName,
                businessType: values.businessType,
                phoneNumber: values.businessPhone,
                email: values.businessEmail,
                ...(values.message ? { message: values.message } : {}),
              };
              const res = await preRegisterPartner(preRegisterData);
              if (res.data.success) {
                setShowSuccess(true);
                resetForm();
              }
            } catch (error) {
              console.error("Error during pre-registration:", error);
              const errorCode = (error as any)?.response?.data?.errorCode;
              if(errorCode === "PROVIDER_EXISTS" || errorCode === "EMAIL_EXISTS"){
                setFieldError("businessEmail", "This email is already registered.");
              }
              if(errorCode === "BUSINESS_NAME_EXISTS"){
                setFieldError("businessName", "This business name is already registered.");
              }
              if(errorCode === "PARTNER_PHONE_NUMBER_EXISTS"){
                setFieldError("businessPhone", "This phone number is already registered.");
              }
              if((error as any)?.response?.data?.code === "VALIDATION_ERROR"){
                setFieldError("businessPhone", "This phone number is invalid.");
              }
            } finally {
              setIsSubmitting(false);
            }
          }}
        >
          {({
            values,
            handleChange,
            handleBlur,
            touched,
            errors,
            isValid,
            dirty,
          }) => (
            <Form>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Typography className="form-label">
                  Business Name
                  <span>*</span>
                </Typography>
                <TextField
                  fullWidth
                  id="businessName"
                  name="businessName"
                  placeholder="Enter business name"
                  value={values.businessName}
                  InputProps={compactInputProps}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.businessName && Boolean(errors.businessName)}
                  helperText={touched.businessName && errors.businessName}
                  sx={{
                    input: {
                      color: "#104145 !important",
                    },
                  }}
                  inputProps={{ maxLength: 50 }}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Typography className="form-label">
                  Business Type
                  <span>*</span>
                </Typography>
                <Select
                  id="businessType"
                  name="businessType"
                  displayEmpty
                  value={values.businessType}
                  onChange={handleChange}
                  slotProps={{
                    root: compactInputProps,
                  }}
                  onBlur={handleBlur}
                  error={touched.businessType && Boolean(errors.businessType)}
                  sx={{
                    input: {
                      color: "#104145 !important",
                    },
                  }}
                  renderValue={(selected) => {
                    if (selected === "") {
                      return (
                        <span style={{ color: "rgba(16, 65, 69, 0.4)" }}>
                          Select a business type
                        </span>
                      );
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="" disabled>
                    Select a business type
                  </MenuItem>
                  {businessTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.type}
                    </MenuItem>
                  ))}
                </Select>
                {touched.businessType && errors.businessType && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{
                      marginLeft: "0px",
                      marginTop: "0px",
                      display: "block",
                    }}
                  >
                    {errors.businessType}
                  </Typography>
                )}
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Typography className="form-label">
                  Business Phone
                  <span>*</span>
                </Typography>
                <Box
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "4px !important",
                      height: "39px !important",
                      maxWidth: "100%",
                    },
                    "& .MuiInputBase-root": {
                      fontSize: "14px",
                      height: "39px !important",
                      padding: "10px 6px !important",
                    },
                    "& .MuiInputAdornment-root": {
                      backgroundColor: "#FFFFFF",
                      borderRadius: "6px 0 0 6px",
                    },
                    "& input": {
                      color: "#104145 !important",
                    },
                  }}
                >
                  <NewPhoneNumberComponent
                    value={values.businessPhone}
                    onChange={(mobileValue: string) =>
                      handleChange({
                        target: {
                          name: "businessPhone",
                          value: mobileValue,
                        },
                      })
                    }
                    countryCode={countryCode}
                    onCountryChange={setCountryCode}
                    placeholder="Enter phone number"
                    error={
                      touched.businessPhone && Boolean(errors.businessPhone)
                    }
                  />
                </Box>
                {touched.businessPhone && errors.businessPhone && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{
                      marginLeft: "0px",
                      marginTop: "0px",
                      display: "block",
                    }}
                  >
                    {errors.businessPhone}
                  </Typography>
                )}
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Typography className="form-label">
                  Business Email
                  <span>*</span>
                </Typography>
                <TextField
                  fullWidth
                  id="businessEmail"
                  name="businessEmail"
                  type="email"
                  placeholder="Enter business email"
                  value={values.businessEmail}
                  onChange={handleChange}
                  InputProps={compactInputProps}
                  onBlur={handleBlur}
                  error={touched.businessEmail && Boolean(errors.businessEmail)}
                  helperText={touched.businessEmail && errors.businessEmail}
                  sx={{
                    input: {
                      color: "#104145 !important",
                    },
                  }}
                  inputProps={{ maxLength: 150 }}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Typography className="form-label">Message</Typography>
                <TextField
                  fullWidth
                  id="message"
                  name="message"
                  placeholder="Type your message here ..."
                  multiline
                  rows={4}
                  value={values.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  sx={{
                    input: {
                      color: "#104145 !important",
                    },
                  }}
                  inputProps={{ maxLength: 255 }}
                />
              </FormControl>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Button
                  color="primary"
                  variant="contained"
                  fullWidth
                  type="submit"
                  disabled={!isValid || !dirty || isSubmitting}
                  className="submit-button"
                  sx={{
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                    }
                  }}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Box>
      {showSuccess && (
        <PlannerSuccessModal
          open={showSuccess}
          onClose={() => setShowSuccess(false)}
          title="Submission Successful"
          subtitle=""
          businessName={""}
          infoText="Thank you for reaching out! Our team will get back to you shortly."
          isConsultant={false}
        />
      )}
    </Box>
  );
};

export default BecomePartnerForm;
